import React from 'react'
import { useSelector } from 'react-redux'
import $t from '../../../../i18n'
import Card from 'components/common/Card'
import { Tooltip } from 'antd'
import benefitsLogo from 'resources/benefitsLogo.png'
import dayjs from 'dayjs'
import config from '../../../../config.json'

import './BenefitsCard.css'
import AnimatedLoadingLabel from 'components/common/AnimatedLoadingLabel'
import { ACCUMULATOR_TYPES, MEMBER_TYPES } from 'consts/coverageBenefitsConsts'

const BenefitsCard = ({ subscription, accumulatorsMap, isFamily }) => {
  const now = dayjs().utc()
  const planSummaryPdf = subscription?.plan_details?.plan_cb_summary_pdf
  const membersAccumulatorsError = useSelector(
    state => state.profile.membersAccumulatorsError
  )
  const isMembersAccumulatorsLoading = useSelector(
    state => state.profile.isMembersAccumulatorsLoading
  )

  const getDeductibleTooltip = () => {
    return (
      <div className="tooltip">
        <div className="tooltip-title-header semi-bold">
          {$t('pages.coverageBenefits.deductibleTooltip.title')}
        </div>
        <div className="tooltip-title-body-header light">
          {$t('pages.coverageBenefits.deductibleTooltip.subtitle')}
        </div>
      </div>
    )
  }

  const getOutOfPocketTooltip = () => {
    return (
      <div className="tooltip">
        <div className="tooltip-title-header semi-bold">
          {$t('pages.coverageBenefits.outOfPocketTooltip.title')}
        </div>
        <div className="tooltip-title-body-header light">
          {$t('pages.coverageBenefits.outOfPocketTooltip.subtitle')}
        </div>
      </div>
    )
  }

  const renderAccumulatorAmount = data => {
    // A null usage or ceiling means there is no record, which is a different, weaker claim
    // than "$0 used" or "$0 limit" — showing a figure would tell a member something about
    // their spend that we simply do not know.
    if (data.usage === null || data.max === null) {
      return $t('pages.coverageBenefits.partialInfoNotAvailable')
    }
    return $t('pages.coverageBenefits.outOf', {
      current: data.usage,
      max: data.max,
    })
  }

  const renderAccumulatorSection = type => {
    if (!accumulatorsMap || accumulatorsMap.size === 0) return null

    return (
      <div className="accumulator-info">
        {Array.from(accumulatorsMap.entries())
          .filter(([accMemberId]) => accMemberId !== MEMBER_TYPES.FAMILY)
          .map(([accMemberId, data]) => (
            <div key={accMemberId} className="member-row">
              <span className="member-name">{data.name}</span>
              <span className="amount">
                {renderAccumulatorAmount(data[type])}
              </span>
            </div>
          ))}
        {isFamily && (
          <div className="family-row">
            <span className="family-label">{$t('common.family')}</span>
            <span className="amount">
              {renderAccumulatorAmount(
                accumulatorsMap.get(MEMBER_TYPES.FAMILY)[type]
              )}
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <Card className="benefits-card">
      <div className="benefits-card-title">
        <div className="benefits-card-title-header">
          <img
            src={`${benefitsLogo}`}
            alt="coverage-benefits"
            className="benefits-image"
          />
          <h2 className="coverage-benefits-card-title semi-bold">
            {$t('pages.coverageBenefits.planSpending')}
          </h2>
        </div>
      </div>
      {planSummaryPdf && (
        <a
          href={planSummaryPdf}
          className="summary-plan-link trackable"
          data-name="summary-plan-link"
          rel="noreferrer"
          target="_blank"
        >
          {$t('pages.coverageBenefits.summaryBenefits')}
        </a>
      )}
      <div className="details-in-card">
        {$t(`pages.coverageBenefits.${ACCUMULATOR_TYPES.DEDUCTIBLE}`)}
        <Tooltip
          title={getDeductibleTooltip()}
          placement="bottom"
          color={'var(--color_primary_t600)'}
        >
          <i className="icon-oinfo"></i>
        </Tooltip>
      </div>
      {isMembersAccumulatorsLoading ? (
        <AnimatedLoadingLabel />
      ) : membersAccumulatorsError ? (
        <div className="error-message">
          {$t('pages.coverageBenefits.partialInfoNotAvailable')}
        </div>
      ) : (
        <div>{renderAccumulatorSection(ACCUMULATOR_TYPES.DEDUCTIBLE)}</div>
      )}
      <div>
        <div className="details-in-card">
          {$t(`pages.coverageBenefits.${ACCUMULATOR_TYPES.OOP}`)}
          <Tooltip
            title={getOutOfPocketTooltip()}
            placement="bottom"
            color={'var(--color_primary_t600)'}
          >
            <i className="icon-oinfo"></i>
          </Tooltip>
        </div>
        {isMembersAccumulatorsLoading ? (
          <AnimatedLoadingLabel />
        ) : membersAccumulatorsError ? (
          <div className="error-message">
            {$t('pages.coverageBenefits.partialInfoNotAvailable')}
          </div>
        ) : (
          <>{renderAccumulatorSection(ACCUMULATOR_TYPES.OOP)}</>
        )}
      </div>
      <div className="disclaimer">
        {$t('pages.coverageBenefits.disclaimer', {
          date: now.format(config.genericDateFormat),
        })}
      </div>
    </Card>
  )
}

export default BenefitsCard
